import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Border,
  Colors,
  Icon,
  Icons,
  Spacing,
  Typography,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import { SpaceType } from '@/__generated__/types';
import { useDashboardScope } from '@/hooks/useDashboardScope';
import { useSpaces } from '@/hooks/useSpaces';
import { useTenantName } from '@/hooks/useTenantName';

import {
  ConnectAppCard,
  InsightsAppCard,
  TwinStudioAppCard,
  WelcomeHeader,
} from './components';
import { APP_CLIENT_IDS } from './constants';
import { useAppAccess } from './hooks/useAppAccess';

const classes = createClasses({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('xl'),
    maxWidth: pxToRem(1320),
    margin: '0 auto',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('md'),
  },
  sectionHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xs'),
  },
  headingIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: pxToRem(32),
    height: pxToRem(32),
    borderRadius: Border.Radius.Circle,
    backgroundColor: Colors.Selected.backgroundActive,
    color: Colors.Neutral.icon,
  },
  heading: {
    margin: 0,
  },
  appsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: spacing('sm'),
    marginTop: `calc(-1 * ${Spacing.xxs})`,
    '@media (max-width: 720px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
});

export const Home: React.FC = () => {
  const { t } = useTranslation();
  const { timeSpan, setTimeSpan, spaceId, spaceType } = useDashboardScope();
  const { spaces, isLoading: isSpacesLoading } = useSpaces();
  const { name: tenantName } = useTenantName();
  const { getAccess } = useAppAccess();

  const scope = spaceId && spaceType ? { spaceId, spaceType } : undefined;
  const buildingCount = isSpacesLoading
    ? undefined
    : spaces.filter((space) => space.type === SpaceType.Building).length;

  return (
    <div className={classes.root}>
      <WelcomeHeader
        timeSpan={timeSpan}
        onTimeSpanChange={setTimeSpan}
        tenantName={tenantName}
        buildingCount={buildingCount}
      />
      <section className={classes.section}>
        <div className={classes.sectionHeading}>
          <span className={classes.headingIcon}>
            <Icon icon={Icons.IconApps} size={20} />
          </span>
          <Typography
            variant="titleMedium"
            component="h2"
            className={classes.heading}
          >
            {t('home.servicesTitle')}
          </Typography>
        </div>
        <InsightsAppCard
          {...getAccess(APP_CLIENT_IDS.insights)}
          scope={scope}
          timeSpan={timeSpan}
        />
        <div className={classes.appsGrid}>
          <TwinStudioAppCard {...getAccess(APP_CLIENT_IDS.twinStudio)} />
          <ConnectAppCard {...getAccess(APP_CLIENT_IDS.connect)} />
        </div>
      </section>
    </div>
  );
};
